import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { Component } from 'react';
import styled from 'styled-components';
import ndsTokens from 'nicollet-tokens';
import { ndsFontReset, sanitizeValue, slugify, getDataAttributes } from '@nicollet/common';
import { Link as StyledLink } from '../Link/index.js';
import { Label } from '../Label/index.js';
import { ErrorMessage } from '../ErrorMessage/index.js';
import { Microcopy } from '../Microcopy/index.js';
import { InputWrapper } from '../InputWrapper/index.js';

const StyledInput = styled.input`
  ${({
  valid,
  errorText
}) => valid === false && errorText && `color: ${ndsTokens.colors.alert.warning}`}
`;
const RightContentWrapper = styled.div`
  ${ndsFontReset()}
  font-size: ${ndsTokens.font.size.text.base};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: ${ndsTokens.space.generic.x10};
  position: absolute;
  top: 8px;
  height: 34px;
  right: 8px;
`;
const LeftContentWrapper = styled.span`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  min-width: ${ndsTokens.space.generic.x6};
  position: absolute;

  /* if there is a label, the top should be pushed down in order to properly space left content under the label when focus in input field */
  top: 8px;
  height: 34px;
  left: 8px;
`;

class Input extends Component {
  constructor(props) {
    super(props);
    this.myRef = React.createRef();
    this.state = {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      hasValue: Boolean(props.value || false),
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      hasFocus: Boolean(props.hasFocus || false),
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      type: props.type || 'text',
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      autoComplete: props.autoComplete || 'on'
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.togglePassword = this.togglePassword.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
  }

  static getDerivedStateFromProps(props) {
    return {
      hasValue: Boolean(props.value)
    };
  }

  componentDidMount() {
    var _a;

    if (this.props.type === 'number') {
      //@ts-ignore
      (_a = this.myRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('wheel', this.handleWheel, {
        passive: false
      });
    }
  }

  componentWillUnmount() {
    var _a;

    if (this.props.type === 'number') {
      //@ts-ignore
      (_a = this.myRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('wheel', this.handleWheel);
    }
  }

  handleWheel(e) {
    e.preventDefault();
  }

  setHasInput(value) {
    this.setState({
      hasValue: Boolean(value)
    });
  }

  getValue(e) {
    return !this.props.disableSanitize ? sanitizeValue(e.target.value) : e.target.value;
  }

  handleChange(e) {
    this.setHasInput(this.getValue(e));

    if (this.props.onChange) {
      this.props.onChange(this.getValue(e), e);
    }
  }

  handleFocus(e) {
    this.setState({
      hasFocus: true
    });

    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  }

  handleBlur(e) {
    const currentValue = this.getValue(e);

    if (currentValue) {
      this.setState({
        hasValue: true,
        hasFocus: false
      });
    } else {
      this.setState({
        hasFocus: false
      });
    }

    if (this.props.onBlur) {
      this.props.onBlur(this.getValue(e), e);
    }
  }

  togglePassword(e) {
    e.preventDefault(); // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing

    const passwordType = this.props.passwordType || 'text';
    this.setState(prevState => ({
      autoComplete: prevState.type === 'password' ? 'off' : 'current-password',
      type: prevState.type === 'password' ? passwordType : 'password'
    }));
  }

  handleKeyPress(e) {
    const {
      onKeyPress,
      onEnterKeyPress
    } = this.props;

    if (onEnterKeyPress && e && (e.code === "Enter"
    /* Enter */
    || e.key === "Enter"
    /* Enter */
    )) {
      onEnterKeyPress(e);
    }

    if (onKeyPress) {
      onKeyPress(e);
    }
  }

  render() {
    var _a;

    const {
      autoCapitalize = this.props.type !== 'text' && typeof this.props.autoCapitalize === 'undefined' ? 'none' : this.props.autoCapitalize,
      // If it's not a text input, don't try to autocapitalize
      autoComplete,
      autoCorrect = this.props.type !== 'text' && typeof this.props.autoCorrect === 'undefined' ? 'off' : this.props.autoCorrect,
      // If it's not a text input, don't try to autoCorrect
      autoFocus,
      className,
      dataAttributes = {},
      'data-test': testId,
      disabled,
      disableLiveRegion,
      errorBackground,
      hasRoleAlert,
      hideLabel = false,
      label,
      id = label ? slugify(label) : '',
      inputMode,
      max,
      maxlength,
      min,
      minlength,
      name = id,
      pattern,
      readOnly,
      required,
      spellCheck = this.props.type !== 'text' && typeof this.props.spellCheck === 'undefined' ? 'false' : this.props.spellCheck,
      // If it's not a text input, don't try to spellCheck
      step,
      type = 'text',
      value,
      allowTogglePassword,
      childRef,
      errorText,
      isValid,
      microcopy,
      screenReader,
      leftContent,
      onKeyDown,
      onKeyUp
    } = this.props;
    const valid = isValid === true ? true : isValid; // only sets valid to true if isValid is explictly set to true, otherwise allows it to be undefined or false - supports three values

    let {
      rightContent
    } = this.props; // Needs to be let so we can inject passwordToggle into this variable, cleans up code further down

    const rawDataAttributes = getDataAttributes(this.props);
    const htmlAttributes = {
      autoCapitalize,
      autoComplete,
      autoCorrect,
      autoFocus,
      className,
      'data-test': testId,
      disabled,
      id,
      inputmode: inputMode,
      max,
      maxLength: maxlength,
      min,
      minLength: minlength,
      name,
      onBlur: this.handleBlur,
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onKeyDown,
      onKeyPress: this.handleKeyPress,
      onKeyUp,
      pattern,
      readOnly,
      required,
      spellcheck: spellCheck ? spellCheck : null,
      step,
      type,
      value
    };
    Object.keys(dataAttributes || {}).forEach(key => {
      htmlAttributes[`data-${key}`] = dataAttributes[key];
    });

    if (htmlAttributes.type === 'password' && allowTogglePassword) {
      rightContent = //eslint-disable-next-line jsx-a11y/anchor-is-valid
      jsx(StyledLink, Object.assign({
        onClick: this.togglePassword
      }, {
        children: this.state.type === 'password' ? 'show' : 'hide'
      }), void 0);
    }

    return jsxs(InputWrapper, Object.assign({
      className: className,
      disabled: disabled,
      hasFocus: this.state.hasFocus,
      hasInput: // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      this.state.hasValue || // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      this.state.hasFocus || ['color', 'date', 'datetime-local', 'month', 'time', 'week'].includes((_a = this.props.type) !== null && _a !== void 0 ? _a : ''),
      leftContent: leftContent,
      rightContent: rightContent,
      valid: valid
    }, {
      children: [jsx(Label, {
        animated: true,
        disabled: disabled,
        disableLiveRegion: disableLiveRegion,
        errorText: errorText,
        hidden: hideLabel,
        htmlFor: id,
        label: label,
        microcopy: microcopy,
        screenReader: screenReader,
        valid: valid
      }, void 0), jsx(StyledInput, Object.assign({}, htmlAttributes, rawDataAttributes, {
        autoComplete: this.state.autoComplete,
        errorText: errorText,
        ref: childRef !== null && childRef !== void 0 ? childRef : this.myRef,
        role: hasRoleAlert ? 'alert' : undefined,
        type: this.state.type,
        valid: valid
      }), void 0), leftContent && jsx(LeftContentWrapper, {
        children: leftContent
      }, void 0), valid === false && jsx(ErrorMessage, Object.assign({
        "aria-hidden": true,
        errorBackground: errorBackground,
        id: `${id}--ErrorMessage`
      }, {
        children: errorText
      }), void 0), microcopy && jsx(Microcopy, Object.assign({
        id: `${id}--Microcopy`
      }, {
        children: microcopy
      }), void 0), rightContent && jsx(RightContentWrapper, {
        children: rightContent
      }, void 0)]
    }), void 0);
  }

}

Input.displayName = 'Input';

export { Input };
